import { useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Download, RotateCcw } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TablePagination } from "../components/TablePagination";
import { inputClass, selectClass } from "../components/FormField";
import { getStoredAccountType } from "../auth";
import { useAdminMedicalActs } from "../hooks/useAdminMedicalActs";
import { cn } from "@/shared/lib/cn";
import type { StandardActCode } from "../types";

type SortKey = "code" | "label" | "specialty" | "category";

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

export function StandardActCodesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.acts.standardCodes;
  const paginationLabels = t.safecheck.tablePagination;
  const { pathname } = useLocation();
  const { acts, loading, error, usingApi, reload } = useAdminMedicalActs();
  const [topSearch, setTopSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>("code");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    void reload();
  }, [pathname, reload]);

  const filtered = useMemo(() => {
    return acts.filter((act) => {
      const haystack =
        `${act.code} ${act.label} ${act.category} ${act.specialty} ${act.description}`.toLowerCase();
      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      return byTopSearch && byTableSearch;
    });
  }, [acts, topSearch, tableSearch]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const cmp = String(a[sortKey]).localeCompare(String(b[sortKey]));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const resetFilters = () => {
    setTopSearch("");
    setTableSearch("");
    setPage(1);
    void reload();
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExportCsv = () => {
    const headers = [
      labels.columns.code,
      labels.columns.name,
      labels.columns.specialty,
      labels.columns.category,
    ];
    const rows = [
      headers.join(","),
      ...sorted.map((act: StandardActCode) =>
        [act.code, act.label, act.specialty, act.category]
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      ),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "actes-standards.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns: { key: SortKey; label: string }[] = [
    { key: "code", label: labels.columns.code },
    { key: "label", label: labels.columns.name },
    { key: "specialty", label: labels.columns.specialty },
    { key: "category", label: labels.columns.category },
  ];

  const renderCell = (value: string) => value || "—";

  return (
    <>
      <Breadcrumbs
        homeHref={getDashboardHomeHref()}
        items={[
          { label: t.safecheck.nav.systemSection },
          { label: labels.title },
        ]}
      />

      {(usingApi || error) && (
        <p className={`mb-3 text-xs ${error ? "text-pink" : "text-muted"}`}>
          {error ?? (loading ? "Chargement…" : `${acts.length} acte(s) API`)}
        </p>
      )}

      <div className="theme-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <input
            value={topSearch}
            onChange={(event) => {
              setTopSearch(event.target.value);
              setPage(1);
            }}
            placeholder={labels.searchPlaceholder}
            className={`${inputClass} w-full max-w-sm`}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <Download size={15} />
              {labels.exportCsv}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
              aria-label={t.safecheck.reset}
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 text-sm text-muted">
            <span>{t.safecheck.show}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className={selectClass}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>{t.safecheck.entries}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">{t.safecheck.searchLabel}</span>
            <input
              value={tableSearch}
              onChange={(event) => {
                setTableSearch(event.target.value);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-[11px] font-semibold tracking-wide text-muted"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="inline-flex items-center gap-1 uppercase"
                    >
                      {column.label}
                      <span className="flex flex-col">
                        <ChevronUp
                          size={10}
                          className={cn(
                            "-mb-1",
                            sortKey === column.key && sortDir === "asc" && "text-primary",
                          )}
                        />
                        <ChevronDown
                          size={10}
                          className={cn(
                            sortKey === column.key && sortDir === "desc" && "text-primary",
                          )}
                        />
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((act, index) => (
                <tr
                  key={act.id}
                  className={cn(
                    "border-b border-border last:border-0 hover:bg-card-muted/30",
                    index % 2 === 1 ? "bg-card-muted/20" : "bg-card",
                  )}
                >
                  <td className="px-4 py-3 text-sm font-semibold text-pink">{act.code}</td>
                  <td className="px-4 py-3 text-sm font-bold uppercase text-foreground">
                    {act.label}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted">{renderCell(act.specialty)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-md bg-muted-foreground px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                      {act.category}
                    </span>
                  </td>
                </tr>
              ))}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-muted">
                    {loading ? "Chargement…" : labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={sorted.length}
          onPageChange={handlePageChange}
          labels={paginationLabels}
        />
      </div>
    </>
  );
}
